import { lazy, ReactElement } from "react";
import PublicRouteOwner from "./PublicRouteOwner";
import ProtectedRouteOwner from "./ProtectedRouteOwner";
import StoreModeGuard from "./StoreModeGuard";

const StoreLogin = lazy(() => import("../pages/store/auth/StoreLogin"));
const StoreLoginMagic = lazy(() => import("../pages/store/auth/StoreLoginMagic"));
const OwnerHomePage = lazy(() => import("../pages/owner/home/OwnerHomePage"));
const ProductDetailsPage = lazy(() => import("../pages/store/products/ProductDetailsPage"));
const StoreProductsOverView = lazy(() => import("../pages/store/products/StoreProductsOverView"));
const AddProduct = lazy(() => import("../pages/store/products/AddProduct"));
const UploadProducts = lazy(() => import("../pages/store/products/UploadProducts"));
const UpdateProduct = lazy(() => import("../pages/store/products/UpdateProduct"));
const AddExtraItems = lazy(() => import("../components/store/products/form/ExtraItemsForm"));
const UploadextraItemsPage = lazy(() => import("../pages/store/products/UploadExtraItems"));
const AddProductInstructions = lazy(() => import("../pages/store/products/AddProductInstructions"));
const AddProductVariant = lazy(() => import("../pages/store/products/AddProductVariant"));
const StoreOffersOverView = lazy(() => import("../pages/store/offers/StoreOffersOverView"));
const AddOfferPage = lazy(() => import("../pages/store/offers/CreateOfferPage"));
const StoreStreaksOverView = lazy(() => import("../pages/store/streaks/StoreStreaksOverView"));
const StoreBookingsOverView = lazy(() => import("../pages/store/bookings/StoreBookingsOverView"));
const StoreOrdersOverView = lazy(() => import("../pages/store/orders/StoreOrdersOverView"));
const OrderDetailsPage = lazy(() => import("../pages/store/orders/OrderDetailsPage"));
const StoreBookingDetailsPage = lazy(() => import("../pages/store/bookings/StoreBookingDetailsPage"));
const StoreOverviewTransactions = lazy(() => import("../pages/store/transactions/StoreOverviewTransactions"));
const StoreReviewOverView = lazy(() => import("../pages/store/review/StoreReviewOverView"));
const UpdateStore = lazy(() => import("../pages/store/updatePages.tsx/UpdateStore"));
const StoreViewVariantCategoriesPage = lazy(() => import("../pages/store/variantCategories/StoreViewVariantCategories"));
const StoreAddVariantCategoryPage = lazy(() => import("../pages/store/variantCategories/StoreAddVariantCategory"));
const StoreCategoriesOverView = lazy(() => import("../pages/store/category/StoreCategoriesOverView"));
const CreateCategoryPage = lazy(() => import("../pages/store/category/CreateCategoryPage"));
const TicketsListPage = lazy(() => import("../pages/store/tickets/TicketsStoreListPage"));
const TicketDetailPage = lazy(() => import("../pages/store/tickets/TicketDetailPage"));
const CreateTicketPage = lazy(() => import("../pages/store/tickets/CreateTicketPage"));
const UploadInstructionsPage = lazy(() => import("../pages/store/products/UploadInstructions"));
const UploadVariantsPage = lazy(() => import("../pages/store/products/UploadVariants"));
const CreateBookingServicePage = lazy(() => import("../pages/store/bookingServices/CreateBookingServicePage"));
const StoreBookingServicesOverView = lazy(() => import("../pages/store/bookingServices/StoreBookingServicesOverView"));
const StoreStaffOverView = lazy(() => import("../pages/store/staff/StoreStaffOverView"));
const StoreLaundryServicesOverView = lazy(() => import("../pages/store/laundryServices/StoreLaundryServicesOverView"));
const CreateLaundryServicePage = lazy(() => import("../pages/store/laundryServices/CreateLaundryServicePage"));
const LaundryServiceDetailsPage = lazy(() => import("../pages/store/laundryServices/LaundryServiceDetailsPage"));
const LaundryOrdersOverView = lazy(() => import("../pages/store/laundry/LaundryOrdersOverView"));
const LaundryOrderDetailsPage = lazy(() => import("../pages/store/laundry/LaundryOrderDetailsPage"));
const StoreCartPage = lazy(() => import("../pages/store/cart/StoreCartPage"));
const StoreCartPreviewPage = lazy(() => import("../shared/cart/pages/CartPreviewRoutePage"));
const StorePriceChangeHistoryPage = lazy(() => import("../pages/store/priceApproval/StorePriceChangeHistoryPage"));
const CategoryOpeningHours = lazy(() => import("../pages/store/category/CategoryOpeningHours"));
const ProductOpeningHours = lazy(() => import("../pages/store/products/ProductOpeningHours"));
const ReorderProductsAndCategories = lazy(() => import("../pages/store/products/ReorderProductsAndCategories"));
const StoreOrdersMapPage = lazy(() => import("../pages/store/orders/StoreOrdersMapPage"));
const PosOrderingScreen = lazy(() => import("../pages/store/pos/PosOrderingScreen"));
const PosCartViewScreen = lazy(() => import("../pages/store/pos/PosCartViewScreen"));
const StoreContractsPage = lazy(() => import("../pages/store/contracts/StoreContractsPage"));
const StoreContractDetailsPage = lazy(() => import("../pages/store/contracts/StoreContractDetailsPage"));

const withOrderFeatureGuard = (element: ReactElement) => (
  <StoreModeGuard requireOrderFeatures>{element}</StoreModeGuard>
);

const withPosFeatureGuard = (element: ReactElement) => (
  <StoreModeGuard requireOrderFeatures requirePosFeatures>{element}</StoreModeGuard>
);

const withBookingFeatureGuard = (element: ReactElement) => (
  <StoreModeGuard requireBookingFeatures>{element}</StoreModeGuard>
);

const withLaundryFeatureGuardFn = (element: ReactElement) => (
  <StoreModeGuard requireLaundryFeatures>{element}</StoreModeGuard>
);

export const storeRoutes = [
  {
    path: "/store/login",
    element: (
      <PublicRouteOwner>
        <StoreLogin />
      </PublicRouteOwner>
    ),
  },
  {
    path: "/store-login",
    element: <StoreLoginMagic />,
  },
  {
    path: "/store/dashboard",
    element: <ProtectedRouteOwner requireStore>
      <OwnerHomePage />
    </ProtectedRouteOwner>
  },
  // products store page
  { path: "/store/products", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<StoreProductsOverView />)}</ProtectedRouteOwner> },
  { path: "/store/products/:productId", element: withOrderFeatureGuard(<ProductDetailsPage />) },
  { path: "/store/products/add-product", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<AddProduct />)}</ProtectedRouteOwner> },
  { path: "/store/products/upload-product", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<UploadProducts />)}</ProtectedRouteOwner> },
  { path: "/store/update-product/:productId", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<UpdateProduct />)}</ProtectedRouteOwner> },
  { path: "/store/products/:productId/extra-items", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<AddExtraItems />)}</ProtectedRouteOwner> },
  { path: "/store/upload-extras", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<UploadextraItemsPage />)}</ProtectedRouteOwner> },
  { path: "/store/products/:productId/instructions", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<AddProductInstructions />)}</ProtectedRouteOwner> },
  { path: "/store/upload-instructions", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<UploadInstructionsPage />)}</ProtectedRouteOwner> },
  { path: "/store/products/:productId/variants", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<AddProductVariant />)}</ProtectedRouteOwner> },
  { path: "/store/upload-variants", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<UploadVariantsPage />)}</ProtectedRouteOwner> },
  {
    path: "/store/products/:productId/opening-hours",
    element: (
      <ProtectedRouteOwner requireStore>
        {withOrderFeatureGuard(<ProductOpeningHours />)}
      </ProtectedRouteOwner>
    ),
  },
  {
    path: "/store/reorder-products-categories",
    element: (
      <ProtectedRouteOwner requireStore>
        {withOrderFeatureGuard(
          <ReorderProductsAndCategories />
        )}
      </ProtectedRouteOwner>
    ),
  },


  //  offers pages for store
  { path: "/store/offers", element: <ProtectedRouteOwner requireStore><StoreOffersOverView /></ProtectedRouteOwner> },
  { path: "/store/offers/add-offer", element: <ProtectedRouteOwner requireStore><AddOfferPage /></ProtectedRouteOwner> },

  //  streaks pages for store
  { path: "/store/streaks", element: <ProtectedRouteOwner requireStore><StoreStreaksOverView /></ProtectedRouteOwner> },

  // orders pages  for store
  { path: "/store/orders", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<StoreOrdersOverView />)}</ProtectedRouteOwner> },
  { path: "/store/orders/:orderId", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<OrderDetailsPage />)}</ProtectedRouteOwner> },
  {
    path: "/store/orders/map",
    element: (
      <ProtectedRouteOwner requireStore>
        {withOrderFeatureGuard(<StoreOrdersMapPage />)}
      </ProtectedRouteOwner>
    ),
  },

  // POS ordering pages for store
  { path: "/store/pos", element: <ProtectedRouteOwner requireStore>{withPosFeatureGuard(<PosOrderingScreen />)}</ProtectedRouteOwner> },
  { path: "/store/pos/carts/:cartId", element: <ProtectedRouteOwner requireStore>{withPosFeatureGuard(<PosCartViewScreen />)}</ProtectedRouteOwner> },

  // bookings pages for store
  { path: "/store/bookings", element: <ProtectedRouteOwner requireStore>{withBookingFeatureGuard(<StoreBookingsOverView />)}</ProtectedRouteOwner> },
  { path: "/store/bookings/:bookingId", element: <ProtectedRouteOwner requireStore>{withBookingFeatureGuard(<StoreBookingDetailsPage />)}</ProtectedRouteOwner> },

  // transactions pages for store
  { path: "/store/transactions", element: <ProtectedRouteOwner requireStore><StoreOverviewTransactions /></ProtectedRouteOwner> },

  // staff pages for store
  { path: "/store/staff", element: <ProtectedRouteOwner requireStore>{withBookingFeatureGuard(<StoreStaffOverView />)}</ProtectedRouteOwner> },

  // booking services pages for store
  { path: "/store/booking-services", element: <ProtectedRouteOwner requireStore>{withBookingFeatureGuard(<StoreBookingServicesOverView />)}</ProtectedRouteOwner> },
  { path: "/store/booking-services/create", element: <ProtectedRouteOwner requireStore>{withBookingFeatureGuard(<CreateBookingServicePage />)}</ProtectedRouteOwner> },

  // laundry services pages for store
  { path: "/store/laundry-services", element: <ProtectedRouteOwner requireStore>{withLaundryFeatureGuardFn(<StoreLaundryServicesOverView />)}</ProtectedRouteOwner> },
  { path: "/store/laundry-services/create", element: <ProtectedRouteOwner requireStore>{withLaundryFeatureGuardFn(<CreateLaundryServicePage />)}</ProtectedRouteOwner> },
  { path: "/store/laundry-services/:serviceId", element: <ProtectedRouteOwner requireStore>{withLaundryFeatureGuardFn(<LaundryServiceDetailsPage />)}</ProtectedRouteOwner> },

  // laundry orders pages for store
  { path: "/store/laundry", element: <ProtectedRouteOwner requireStore>{withLaundryFeatureGuardFn(<LaundryOrdersOverView />)}</ProtectedRouteOwner> },
  { path: "/store/laundry/:orderId", element: <ProtectedRouteOwner requireStore>{withLaundryFeatureGuardFn(<LaundryOrderDetailsPage />)}</ProtectedRouteOwner> },

  // cart pages for store
  { path: "/store/cart", element: <ProtectedRouteOwner requireStore><StoreCartPage /></ProtectedRouteOwner> },
  { path: "/store/cart/preview", element: <ProtectedRouteOwner requireStore><StoreCartPreviewPage role="store" /></ProtectedRouteOwner> },

  // store reviews page
  { path: "/store/reviews", element: <ProtectedRouteOwner requireStore><StoreReviewOverView /></ProtectedRouteOwner> },

  // price changes history
  { path: "/store/price-changes", element: <ProtectedRouteOwner requireStore><StorePriceChangeHistoryPage /></ProtectedRouteOwner> },

  // store settings
  { path: "/store/settings", element: <ProtectedRouteOwner redirectLink="/store/login?redirect=/store/settings"><UpdateStore /></ProtectedRouteOwner> },


  // store variant categories pages
  { path: "/store/variant-categories", element: <ProtectedRouteOwner redirectLink="/store/login">{withOrderFeatureGuard(<StoreViewVariantCategoriesPage />)}</ProtectedRouteOwner> },
  { path: "/store/add-variantCategory", element: <ProtectedRouteOwner redirectLink="/store/login">{withOrderFeatureGuard(<StoreAddVariantCategoryPage />)}</ProtectedRouteOwner> },


  // catgories pages for store
  { path: "/store/categories", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<StoreCategoriesOverView />)}</ProtectedRouteOwner> },
  { path: "/store/add-category", element: <ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<CreateCategoryPage />)}</ProtectedRouteOwner> },
  { path: "/store/categories/:categoryId/opening-hours", element: (<ProtectedRouteOwner requireStore>{withOrderFeatureGuard(<CategoryOpeningHours />)}</ProtectedRouteOwner>), },

  // tickets pages for store
  { path: "/store/tickets", element: <ProtectedRouteOwner requireStore><TicketsListPage /></ProtectedRouteOwner> },
  { path: "/store/tickets/:ticketId", element: <ProtectedRouteOwner requireStore><TicketDetailPage /></ProtectedRouteOwner> },
  { path: "/store/add-ticket", element: <ProtectedRouteOwner requireStore><CreateTicketPage /></ProtectedRouteOwner> },

  // contracts pages for store
  { path: "/store/contracts", element: <ProtectedRouteOwner requireStore><StoreContractsPage /></ProtectedRouteOwner> },
  { path: "/store/contracts/:contractId", element: <ProtectedRouteOwner requireStore><StoreContractDetailsPage /></ProtectedRouteOwner> },

];
